/** Phase 9 (AI, recommendations, demand forecast) UI and error texts — English. See ai.tr.ts. */
export const aiEn = {
  ui: {
    nav: {
      group: 'AI',
      settings: 'AI settings',
      recommendations: 'Product recommendations',
      forecast: 'Demand forecast',
      studio: 'Content Studio',
      copilot: 'Copilot (business assistant)',
      seo: 'SEO suggestions',
      ltv: 'Predicted customer value',
      youtube: 'YouTube videos',
    },
    availability: {
      ready: 'Ready',
      not_configured: 'Setup required',
      disabled: 'Off (awaiting owner approval)',
      budget_exhausted: 'Monthly limit reached',
      daily_limit: 'Daily request limit reached',
    },
    purposes: {
      product_description: 'Product description',
      merchant_title: 'Google product title',
      translation: 'Translation',
      article_plan: 'Article plan',
      article_headings: 'Article headings',
      article_draft: 'Article draft',
      article_meta: 'SEO title and description',
      alt_text: 'Image alt text',
      image: 'Image generation',
      email_draft: 'Email draft',
      ad_copy: 'Ad copy',
      ad_suggestions: 'Ad suggestions',
      support_reply: 'Support reply draft',
      copilot: 'Copilot',
    },
    requestStatus: { pending: 'In progress', ok: 'Completed', error: 'Error' },
    providerNames: { anthropic: 'Anthropic Claude', openai: 'OpenAI Images' },
    recommendations: {
      statusTitle: 'Status',
      statusLead:
        "Recommendations use no AI and are free: they are computed every night at 04:25 from the store's real paid orders. With too little data the storefront section is hidden.",
      run: 'Compute now',
      ran: '{{orders}} orders analysed, {{pairs}} relations saved.',
      stats: '{{pairs}} relations · {{products}} products with recommendations',
      runsTitle: 'Recent runs',
      when: 'Time',
      trigger: 'Started by',
      scheduled: 'Nightly job',
      manualBy: 'Manual ({{name}})',
      orders: 'Orders',
      pairsKept: 'Saved / found relations',
      result: 'Result',
      status: { ok: 'Completed', error: 'Error' },
      noStore: 'No store',
      noStoreLead: 'Recommendations are computed per store. First create a store under Business → Stores.',
      noRuns: 'No runs yet.',
      settingsTitle: 'Settings',
      toggles: {
        alsoBought: 'Product page: "Customers who bought this also bought"',
        cart: 'Cart: "You may also add"',
        replenishment: 'My account: "Time to reorder"',
        includeB2b: 'Include dealer orders',
      },
      toggleHints: {
        alsoBought: 'Products often bought in the same order (complementary products are not repeated).',
        cart: 'Products often bought with the cart items; items already in the cart are hidden.',
        replenishment: 'Estimated reorder date for regularly bought products (shown only to the customer).',
        includeB2b: 'Wholesale orders can distort retail relations; off by default.',
      },
      windowDays: 'Period analysed (days)',
      minPairBuyers: 'Minimum distinct buyers',
      minPairBuyersHint:
        "A relation is used only if it appears in orders of at least this many different customers (so no single customer's purchases can be read from it). At least 2.",
      minConfidence: 'Minimum confidence (%)',
      minConfidenceHint:
        'Share of buyers of the product who also bought the other one. Filters out coincidences.',
      maxPerProduct: 'Maximum recommendations per product',
      save: 'Save settings',
      saved: 'Settings saved. They apply from the next run.',
      pairsTitle: 'Strongest relations',
      pairsLead:
        "Each product's strongest relation. Confidence: share of buyers who also bought the other. Lift: how many times more often than chance they are bought together (above 1).",
      product: 'Product',
      related: 'Bought together',
      buyers: 'Distinct buyers',
      confidence: 'Confidence',
      lift: 'Lift',
      noPairs: 'No recommendations yet',
      noPairsLead:
        'Recommendations appear automatically once enough orders accumulate (several different customers buying the same two products together).',
    },
    forecast: {
      lead: 'Uses no AI: computed on every view from weekly sales of real paid, non-cancelled orders. Units not yet received on open purchase orders are subtracted from the suggestion. Seasonality and campaigns are not modelled; no purchase order is created.',
      counts: {
        stockout: 'Out of stock',
        reorder: 'Time to reorder',
        on_order: 'Ordered',
        expiry_risk: 'Expiry risk',
        overstock: 'Overstock',
        ok: 'Sufficient',
        no_sales: 'No sales',
      },
      accuracy: 'Forecast error over the last 4 weeks (WAPE): {{pct}}% · {{count}} variants',
      accuracyNone: 'At least 8 weeks of sales history is needed to measure accuracy.',
      totalSuggested: 'Total suggested order: {{count}} units',
      totalOnOrder: 'On order and requested: {{count}} units',
      filterStatus: 'Status',
      all: 'All',
      search: 'Search product or SKU',
      csv: 'Download CSV',
      product: 'Product',
      sold28: 'Last 28 days',
      daily: 'Daily forecast',
      available: 'Available stock',
      onOrder: 'On order',
      requestedNote: '{{count}} requested',
      cover: 'Cover',
      coverDays: '{{days}} days',
      suggested: 'Suggested order',
      status: 'Status',
      method: 'Method',
      methods: {
        none: 'No sales',
        new: 'New product (average)',
        ses: 'Regular (exponential smoothing)',
        croston: 'Intermittent (Croston)',
      },
      expiry: 'Expiry risk: lot {{lot}} on {{date}} — {{count}} units may not sell',
      excluded: 'Excluded from forecast',
      customSafety: 'Custom safety stock: {{days}} days',
      leadLine: 'Lead time {{days}} days ({{source}})',
      leadSources: {
        variant: 'product-specific',
        supplier: 'from supplier',
        supplierNamed: 'supplier: {{name}}',
        default: 'default',
      },
      adjust: 'Adjust',
      empty: 'No products to show',
      emptyLead: 'The list is empty when there are no active variants or nothing matches the filter.',
      variantTitle: '{{product}} — forecast setting',
      leadTime: 'Lead time (days)',
      safety: 'Safety stock (days)',
      defaultHint: 'Leave empty to use the business default ({{days}} days).',
      supplierHint:
        'Leave empty to use the lead time of the supplier on the latest purchase order ({{days}} days).',
      exclude: 'Exclude from forecast (one-off or discontinued product)',
      variantSaved: 'Variant setting saved.',
      settingsTitle: 'Defaults',
      historyWeeks: 'History analysed (weeks)',
      defaultLead: 'Default lead time (days)',
      defaultSafety: 'Default safety stock (days)',
      reviewDays: 'Review period (days)',
      reviewHint: 'The suggested quantity aims to cover demand for lead time + safety + this period.',
      overstockDays: 'Overstock threshold (days)',
      includeB2b: 'Count dealer orders as demand',
      save: 'Save defaults',
      saved: 'Defaults saved.',
    },
    studio: {
      lead: 'Prepare an article in 27 steps: from topic and sources to outline, from draft to SEO and images, from quality check to post-publication performance. AI steps are optional; every field can be filled by hand. Publishing, editor and veterinarian approval happen on the blog screen.',
      create: 'New article project',
      createTitle: 'New article project',
      createSubmit: 'Create project',
      filter: 'Show',
      filters: { active: 'In progress', exported: 'Exported to blog', archived: 'Archive' },
      statuses: { draft: 'In progress', exported: 'Exported to blog', archived: 'Archived' },
      empty: 'No article projects yet',
      emptyLead: 'Start with "New article project" by entering the topic and audience.',
      words: '{{count}} words',
      updated: '{{date}} · {{name}}',
      healthBadge: 'Health content',
      aiAssisted: 'AI-assisted',
      back: 'All projects',
      save: 'Save',
      saved: 'Project saved.',
      unsaved: 'There are unsaved changes.',
      saveFirst: 'Save your changes first.',
      stepsTitle: 'Process: {{done}} / {{total}} steps',
      stepDone: 'done',
      stepOpen: 'open',
      stepAi: 'AI was used in this step',
      steps: {
        topic: 'Topic',
        audience: 'Audience',
        market: 'Target country and language',
        keyword: 'Keyword',
        intent: 'Search intent',
        sources: 'Sources',
        duplicates: 'Duplicate check',
        plan: 'Article plan',
        headings: 'H2/H3 headings',
        draft: 'Draft',
        sourceCheck: 'Source verification',
        internalLinks: 'Internal links',
        relatedProducts: 'Related products',
        seoTitle: 'SEO title',
        metaDescription: 'Meta description',
        slug: 'URL slug',
        category: 'Category',
        tags: 'Tags',
        schema: 'Structured data',
        cover: 'Cover image',
        images: 'Content images',
        altText: 'Alt texts',
        quality: 'Quality check',
        approval: 'Editor and veterinarian approval',
        schedule: 'Publish / schedule',
        performance: 'Performance tracking',
        updates: 'Update suggestions',
      },
      sec: {
        brief: 'Topic and target (steps 1–5)',
        research: 'Sources and duplicate check (steps 6, 7, 11)',
        outline: 'Plan and headings (steps 8–9)',
        draft: 'Draft, internal links and related products (steps 10, 12–13)',
        seo: 'SEO (steps 14–19)',
        images: 'Images (steps 20–22)',
        quality: 'Quality check (step 23)',
        publish: 'Approval and publishing (steps 24–25)',
        performance: 'Performance and updates (steps 26–27)',
      },
      f: {
        title: 'Working title',
        topic: 'Topic',
        topicHint: 'The question the article answers or the subject it covers.',
        audience: 'Audience',
        country: 'Target country',
        countryHint: 'Two-letter country code (TR, DE…).',
        keyword: 'Keyword',
        keywordPool: 'Pick from keyword pool',
        keywordNone: 'None (type it)',
        intent: 'Search intent',
        intentNone: 'Not specified',
        health: 'Health / supplement content (requires veterinarian approval)',
        sourceUrl: 'Source URL',
        sourceTitle: 'Source name',
        sourceNote: 'Note',
        plan: 'Plan',
        headings: 'Headings',
        headingLevel: 'Level',
        headingText: 'Heading',
        draft: 'Draft (Markdown)',
        draftHint:
          '{{count}} words. ## for H2, ### for H3; [text](/urun/slug) for internal links. Raw HTML is not executed.',
        relatedProducts: 'Related products',
        seoTitle: 'SEO title',
        slug: 'Slug',
        metaDescription: 'Meta description',
        category: 'Blog category',
        categoryNone: 'Uncategorised',
        tags: 'Tags',
        tagsHint: 'Comma separated (max 10).',
        cover: 'Cover image',
        coverAlt: 'Cover alt text',
        images: 'Content images',
        imageReplace: 'Replace image',
        imageAlt: 'Alt text',
        imageAdd: 'Add image',
        aiDisclosure: 'Add a "prepared with AI assistance" note at the end of the article',
      },
      intents: {
        informational: 'Looking for information',
        commercial: 'Comparing',
        transactional: 'Wants to buy',
        navigational: 'Looking for a specific page',
      },
      sourcesLead:
        'Add the sources the article relies on. AI may cite only these; it cannot invent sources, statistics or studies. Sources are exported to the blog post’s "References".',
      addSource: 'Add source',
      removeSource: 'Remove source',
      verify: 'Verify sources',
      verified: 'Sources checked.',
      checkedAt: 'checked {{date}}',
      sourceStatus: {
        ok: 'Reachable',
        unreachable: 'Unreachable',
        blocked: 'Blocked (internal network)',
        http_error: 'Page returned an error',
        not_html: 'Not a page',
        invalid_url: 'Invalid URL',
      },
      duplicatesTitle: 'Duplicate check',
      duplicatesNotChecked: 'Not checked yet.',
      duplicatesNone: 'No post or page with a similar title in the store.',
      duplicatesChecked: 'Duplicate check done.',
      similarity: '{{pct}}% similar',
      checkDuplicates: 'Find similar content',
      aiPlan: 'Plan and headings with AI',
      aiDraft: 'Draft with AI',
      aiMeta: 'SEO suggestion with AI',
      aiDone: 'AI suggestion saved. Review and edit it.',
      overwrite: 'Overwrite',
      overwriteConfirm:
        'This field has content. The AI suggestion will replace it (the old version is not kept). Continue?',
      claimsFound: '{{count}} health claim phrases found in the suggestion; fix them before publishing:',
      masked: '{{count}} personal data items were masked before sending to the provider.',
      truncated: 'The response was cut at the length limit; check the ending.',
      addHeading: 'Add heading',
      removeHeading: 'Remove heading',
      linksTitle: 'Internal links',
      linksNone: 'No internal links selected yet.',
      linkTypes: { product: 'Product', category: 'Category', post: 'Post' },
      removeLink: 'Remove link {{title}}',
      suggest: 'Suggest from the store',
      suggestNone: 'No published product, category or post matches the topic.',
      add: 'Add',
      added: 'Added',
      relatedSuggested: 'Suggested products:',
      chars: '{{count}} / {{max}} characters',
      schemaNote:
        'Structured data (BlogPosting) is generated automatically from title, description, cover and dates when the blog post is published.',
      coverSet: 'Cover selected',
      imagesNote:
        'The cover becomes the blog post cover; content images are placed in the draft with "Add to draft" and exported with it.',
      aiCover: 'Cover image with AI',
      aiImage: 'Content image with AI',
      imagePrompt: 'What should the image show?',
      imagePromptHint:
        'Be short and concrete (e.g. "happy golden retriever running in a park, morning light"). No text, logos, brands or recognisable people are added.',
      imageStyle: 'Style',
      imageStyles: { photo: 'Realistic photo', illustration: 'Illustration' },
      imageSize: 'Size',
      imageSizes: { '1536x1024': 'Landscape (3:2)', '1024x1024': 'Square', '1024x1536': 'Portrait (2:3)' },
      imageCost: 'Paid request; spending per image never exceeds the cap in AI settings.',
      generate: 'Generate image',
      imageDone: 'Image generated and added. Write its alt text and save.',
      aiGenerated: 'AI-generated',
      aiAlt: 'Alt text with AI',
      altDone: 'Alt text suggestion filled in; check it against the image and save.',
      insertToDraft: 'Add to draft',
      inserted: 'Image added at the end of the draft; move it where you want and save.',
      provenance: 'Source and licence',
      provenanceTitle: 'Image source and licence',
      provenanceSource: 'Source',
      provenanceSources: {
        own: 'Our own photo / design',
        licensed: 'Licensed (stock image)',
        supplier: 'Supplier / brand',
        other: 'Other',
        ai_generated: 'AI-generated',
      },
      provenanceNone: 'No source information.',
      provenanceLicense: 'Licence',
      provenanceLicenseHint: 'E.g. "Stock standard licence", "CC BY 4.0".',
      provenanceCredit: 'Copyright / credit',
      provenanceUrl: 'Source URL',
      provenanceSaved: 'Source information saved.',
      provenanceAiLocked:
        'The source of an AI-generated image cannot be changed; it is shown with an "AI-generated image" note on the storefront.',
      provenanceModel: 'Model: {{model}} · {{date}}',
      qualityStatus: { pass: 'OK', warn: 'Warning', fail: 'Missing' },
      quality: {
        title_length: 'Title is {{length}} characters (30–70 recommended).',
        meta_length: 'Meta description is {{length}} characters (70–170 recommended).',
        slug: 'Slug set.',
        word_count: 'Draft has {{words}} words (minimum {{min}}).',
        h1_in_body: 'No H1 in the draft (the title is added separately).',
        h2_count: '{{count}} H2 headings (at least 2).',
        keyword_title: 'Keyword appears in the title.',
        keyword_intro: 'Keyword appears in the intro paragraph.',
        keyword_heading: 'Keyword appears in at least one subheading.',
        keyword_density: 'Keyword density {{percent}}% (max 3%).',
        keyword_missing: 'No keyword set.',
        internal_links: 'Internal links: {{count}} in the draft (at least one recommended).',
        sources_missing: 'No sources (required for health content).',
        sources_broken: '{{count}} sources unreachable.',
        sources_unchecked: '{{count}} sources not verified yet.',
        sources_ok: '{{count}} sources verified.',
        cover: 'Cover image and alt text.',
        image_alt: '{{count}} images missing alt text.',
        health_claims: 'Disease prevention/treatment claims: {{count}} phrases.',
        duplicates_unchecked: 'Duplicate check not done.',
        duplicates_similar: 'Very similar content exists: {{title}}.',
        duplicates_ok: 'No very similar content.',
        expert_review: 'Veterinary expert reviewer approval will be required before publishing.',
      },
      qualityAfterSave: 'The quality check reflects the saved version.',
      publishLead:
        'Export creates a draft in the blog or updates the linked draft. Publishing, editor and veterinary expert approval and scheduling happen on the blog screen; the live post does not change on export.',
      export: 'Export to blog post',
      reexport: 'Update blog draft',
      exported: 'Blog draft updated.',
      openPost: 'Open blog post',
      archive: 'Archive project',
      archiveConfirm: 'Archive the project? The linked blog post is not affected.',
      archivedDone: 'Project archived.',
      perf: {
        not_exported: 'Performance appears after the article is exported to the blog and published.',
        not_live: 'The blog post is not live yet.',
        no_permission: 'You are not allowed to view Search Console data.',
        not_linked:
          'Search Console is not connected (Setup required): connect it on the Google connections screen.',
        no_data: 'Search Console data has not been synced yet.',
      },
      perfRange: 'Latest data: {{from}} – {{to}}',
      perfPeriod: 'Period',
      periods: { current: 'Last 28 days', previous: 'Previous 28 days' },
      clicks: 'Clicks',
      impressions: 'Impressions',
      ctr: 'CTR',
      position: 'Average position',
      notComparable: 'Not enough data for the previous period.',
      queries: 'Searches leading to this post (last 28 days)',
      queryStats: '· {{clicks}} clicks · {{impressions}} impressions',
      suggestionsTitle: 'Update suggestions',
      suggestionsNone: 'No suggestions right now.',
      suggestions: {
        stale: 'Not updated for {{days}} days; review the facts and sources.',
        traffic_drop:
          'Clicks dropped compared with the previous 28 days ({{previous}} → {{current}}). Check search queries and freshness.',
        low_ctr:
          'CTR is {{ctr}}% over {{impressions}} impressions: make the SEO title and meta description clearer.',
        near_first_page:
          'Average position {{position}}: close to the first page. Expand the content and link to it from related pages.',
        missing_queries: 'Search queries not covered in the post: {{queries}}. Add a section if relevant.',
        sources_recheck: 'Sources have not been verified for 6 months; check the links again.',
      },
    },
    drafts: {
      copy: 'Copy',
      copied: 'Copied.',
      generate: 'Generate draft',
      again: 'Generate again',
      apply: 'Apply to fields',
      applied: 'Draft written to the form; check it and save.',
      reviewNote:
        'This is a draft based only on the product’s saved data. Read it before publishing; health content goes through veterinary approval.',
      productDescription: 'Description draft with AI',
      productLead:
        'Short and long descriptions are written from the product’s saved data (name, brand, species, category, attributes, composition, usage, warnings). Paid request.',
      tone: 'Tone',
      tones: { informative: 'Informative', friendly: 'Friendly' },
      notes: 'Point to emphasise (optional)',
      translate: 'Translate with AI',
      translateFrom: 'Source language',
      translated: 'Translation written to the form; check it and save.',
      email: 'Subject ideas with AI',
      goal: 'Campaign goal',
      goalHint: 'E.g. "Remind customers about joint support products in winter".',
      audience: 'Audience (optional)',
      products: 'Featured products (optional)',
      subjects: 'Subject ideas',
      useAsA: 'Use as subject',
      useAsB: 'Use as subject B',
      preheader: 'Preheader',
      use: 'Use',
      intro: 'Intro paragraph',
      introNote: 'You can copy the intro paragraph into the email template text.',
      supportReply: 'Reply draft with AI',
      replyDone: 'Reply draft written; read and edit it, then send.',
      adSuggestions: 'Ad recommendations',
      adSuggestionsLead:
        'Rule-based findings from real order revenue (free). Optionally, AI summarises the findings using only these numbers (paid). Budget and publishing changes always go through a change request and approval.',
      period: 'Period',
      lastDays: 'Last {{days}} days',
      findings: 'Show findings',
      summarize: 'Summarise with AI',
      noAdData: 'No ad data in this period. Connect an ad account or upload a report file.',
      noFindings: 'No findings need attention.',
      findingKinds: {
        no_orders: 'Spend without orders',
        roas_below_1: 'ROAS below 1',
        low_ctr: 'Low CTR',
        scale_candidate: 'Can scale',
        anomaly: 'Anomaly',
      },
      anomalies: {
        no_clicks: 'spend without clicks',
        spend_spike: 'spend spike',
        ctr_drop: 'CTR dropped',
        sync_failed: 'sync failed',
        sync_stale: 'data not current',
      },
      aiSummary: 'AI summary',
      adChangeNote:
        'Recommendation only: no ad is created and no budget or status changes. Create a change request and submit it for approval.',
      adCopy: 'Ad copy draft',
      product: 'Product',
      platform: 'Platform',
      platforms: { google: 'Google responsive search ad', meta: 'Meta (Facebook / Instagram)' },
      pickProduct: 'Select a product first.',
      primaryTexts: 'Primary texts',
      headlines: 'Headlines',
      descriptions: 'Descriptions',
      maxChars: 'max {{max}} characters',
      dropped: '{{count}} lines were dropped for exceeding the character limit.',
      adEmpty: 'No usable copy',
      adEmptyLead: 'Try generating again.',
    },
    copilot: {
      lead: 'Insights computed from your data (free, no AI) and an assistant that answers using that data. Copilot never changes any record; only data you are allowed to see is used.',
      period: 'Period',
      copyReport: 'Copy as management report',
      reportTitle: 'Management summary ({{from}} – {{to}})',
      insightsTitle: 'Insights',
      noInsights: 'No insights yet',
      noInsightsLead: 'They appear here as sales, stock or ad data accumulates.',
      severity: { good: 'Good', info: 'Info', warn: 'Attention' },
      sectionsNote: 'Sections excluded because you lack permission: {{hidden}}.',
      sections: {
        stock: 'stock',
        seo: 'SEO',
        customers: 'customer segments',
        ads: 'ads',
        cost: 'cost and margin',
        finance: 'net profit',
      },
      insights: {
        no_sales: 'No paid orders in this or the previous period; no sales insight possible.',
        revenue_change:
          'Product revenue {{current}} (previous {{previous}}, change {{change}}%); {{orders}} orders (previous {{previousOrders}}).',
        aov_change: 'Average order {{current}} (previous {{previous}}, change {{change}}%).',
        decline_drivers: 'Products contributing most to the decline: {{products}}.',
        refund_rate: 'Refund/cancellation rate {{rate}}%: review the return reasons.',
        new_customers: '{{share}}% of orders are from new customers ({{count}} orders).',
        margin_down: 'Gross margin {{previous}}% → {{current}}%: check cost increases or discounts.',
        margin_unknown: 'Margin not available: some sold products have no cost entered.',
        quiet_days:
          'Orders on only {{active}} of {{days}} days in this period; the previous period was steadier.',
        stock_needs: '{{stockout}} variants out of stock, {{reorder}} need reordering: {{items}}.',
        expiry_risk: '{{count}} variants risk stock that cannot be sold before expiry.',
        seo_not_linked: 'Search Console is not connected; SEO opportunities cannot be computed.',
        seo_opportunities:
          'Searches close to the first page but rarely clicked (impressions, position): {{queries}}.',
        segment_at_risk:
          '{{count}} valuable customers are about to be lost (RFM "at risk" and "can’t lose"): consider a win-back campaign.',
        segment_champions: '{{count}} champion customers: invite them to loyalty or referral programmes.',
        ads_roas: 'Ad spend {{spend}}, product revenue from ads {{revenue}} (ROAS {{roas}}).',
        ads_anomalies: '{{count}} ad anomalies: check the ads centre.',
        net_profit:
          'Net profit {{current}} (previous period {{previous}}, change {{change}}%); net margin {{margin}}%.',
        net_profit_partial:
          'Net profit uses incomplete data: sales without cost {{revenue}}, {{shipments}} shipments without shipping cost, {{payments}} payments without fee (Reports → Finance report).',
      },
      salesTitle: 'Sales summary ({{from}} – {{to}})',
      metric: 'Metric',
      current: 'This period',
      previous: 'Previous period',
      metrics: {
        orders: 'Orders',
        revenue: 'Product revenue (excl. VAT and shipping)',
        refunded: 'Refunds / cancellations',
        newCustomers: 'New customers',
      },
      topProducts: 'Top sellers',
      profitTitle: 'Net profit ({{from}} – {{to}})',
      profitLead:
        'Same calculation as the finance report: excluding VAT, in Turkish lira. Net profit = gross profit − shipping − payment fees − ads − lost disputes − operating expenses.',
      profitMetrics: {
        revenueMinor: 'Product revenue (excl. VAT)',
        grossProfitMinor: 'Gross profit',
        shippingMinor: 'Shipping cost',
        feesMinor: 'Payment fees',
        adsMinor: 'Ad spend',
        disputesMinor: 'Lost disputes',
        expensesMinor: 'Operating expenses',
        netProfitMinor: 'Net profit',
        netBps: 'Net margin',
      },
      percent: '{{value}}%',
      askTitle: 'Ask Copilot',
      askLead:
        'Answers use only the computed data above (no customer names, emails or addresses are sent). If data is missing Copilot says so; it never takes action. Each question is a paid request.',
      conversation: 'Conversation',
      you: 'You',
      copilot: 'Copilot',
      question: 'Your question',
      questionHint:
        'E.g. "Why did sales drop?", "Which products should I restock?", "Give me 3 recommendations for this month."',
      ask: 'Ask',
      clear: 'Clear conversation',
    },
    seo: {
      lead: 'AI-free suggestions from your keyword pool and Search Console data: automatic clusters, topic map, long-tail searches, content gaps and Google product titles.',
      notSynced:
        'No Search Console data: impressions and long-tail suggestions stay empty. Connect it on the Google connections screen.',
      clustersTitle: 'Automatic clusters',
      noProposals: 'No new clusters to suggest.',
      emptyPool: 'The keyword pool is empty. Add keywords under SEO → Keywords.',
      keywordCount: '({{count}} keywords)',
      applyClusters: 'Apply {{count}} selected clusters',
      applied: 'Cluster updated for {{count}} keywords.',
      topicsTitle: 'Topic map (pillar and supporting content)',
      noTopics: 'No keywords with a cluster. Apply clusters first.',
      pillar: 'main keyword: {{keyword}} → {{path}}',
      noPage: 'no page',
      link: { yes: 'Links to the pillar', no: 'Missing link to the pillar', unknown: 'Not checked' },
      missingContent: '{{count}} keywords have no target page.',
      longTailTitle: 'Long-tail searches',
      noLongTail: 'No search with 3+ words and 10+ impressions outside the pool.',
      query: 'Search',
      pick: 'Select {{query}}',
      addToPool: 'Add {{count}} selected searches to the pool',
      added: 'Added to the pool.',
      gapsTitle: 'Content gaps',
      noGaps: 'Every keyword has a target page.',
      createProject: 'Create article project',
      openProject: 'Open project',
      titlesTitle: 'Google product titles',
      titlesLead:
        'The title sent to Merchant Center: max 150 characters, key information first, brand and product type, no promotional text or all caps. If empty, the product name is sent.',
      onlyIssues: 'Only those with issues',
      noTitleIssues: 'No title issues.',
      currentTitle: 'Current title',
      suggested: 'Suggestion',
      aiSuggested: 'AI suggestion',
      applyTitle: 'Apply',
      aiTitle: 'Title with AI',
      resetTitle: 'Use product name',
      titleSaved: 'Google title saved; it applies from the next feed upload.',
      issues: {
        too_long: 'Over 150 characters',
        too_short: 'Too short',
        promo_text: 'Promotional text',
        all_caps: 'All caps',
        brand_missing: 'Brand missing',
        symbols: 'Unnecessary symbols / emoji',
        health_claim: 'Health claim',
      },
    },
    youtube: {
      lead: 'Public details of the YouTube videos used on products and blog posts are read with the YouTube Data API (read-only, free daily quota; not AI). The storefront gets video information for search engines (thumbnail, upload date, duration); removed, embedding-disabled or blocked-in-Türkiye videos are not shown on the storefront.',
      connectionTitle: 'Connection',
      notConfigured:
        'No YouTube Data API key yet. Videos keep showing on the storefront as before; only video details and warnings are missing.',
      connectionError:
        'The YouTube key was rejected or the API is disabled. Check the key on the Integrations screen and test again.',
      toIntegrations: 'Enter the key on the Integrations screen',
      connected: 'Connected',
      lastSync: 'Last sync: {{date}}',
      neverSynced: 'Not synced yet (runs every night at 05:50).',
      syncNow: 'Sync now',
      synced: 'Details of {{count}} videos updated.',
      logsTitle: 'Recent syncs',
      outcome: { success: 'Success', failure: 'Error', blocked: 'Blocked' },
      logOk: '{{videos}} videos, {{requests}} requests',
      videosTitle: 'Videos in use',
      noVideos: 'No YouTube videos on products or blog posts.',
      summary: '{{count}} videos · {{problems}} with problems',
      onlyIssues: 'Only those with problems',
      status: {
        available: 'Available',
        processing: 'Processing',
        not_embeddable: 'Not embeddable',
        region_blocked: 'Blocked in Türkiye',
        unavailable: 'Removed or private',
        unknown: 'No details yet',
      },
      problem: {
        not_embeddable: 'The owner disabled playback on other sites; it is hidden on the storefront.',
        region_blocked: 'The video cannot be watched in Türkiye; it is hidden on the storefront.',
        unavailable:
          'The video was deleted, made private or rejected; it is hidden on the storefront. Remove or replace it on the product or post.',
      },
      published: 'Published: {{date}}',
      open: 'Open on YouTube',
      usedBy: 'Used on:',
      product: 'Product: {{name}}',
      post: 'Post: {{name}}',
    },
    ltv: {
      lead: 'Expected spend of customers over the next 12 months, computed from their order history with an explainable model (no AI, no external service). Updated with the RFM computation (nightly).',
      empty: 'No computed customer data yet',
      emptyLead: 'Appears here after RFM is computed on the customer segments screen.',
      summaryTitle: 'Summary',
      computedAt: 'Last computed',
      customers: 'Customers',
      modeledOf: 'Prediction for {{modeled}} / {{total}} customers',
      total: 'Expected total spend in 12 months (existing customers)',
      repeatRate: 'Repeat purchase rate',
      repeatUnknown: 'No prediction for one-time customers (needs at least {{min}} customers; now {{count}})',
      repeatValue: '{{rate}}% · median interval of repeaters {{days}} days',
      top10: 'Share of the most valuable 10% in expected spend',
      cac: 'Acquisition cost from ads (last 90 days)',
      cacValue: '{{cac}} per new customer · expected 12-month value of a new customer {{ltv}}',
      segmentsTitle: 'By segment',
      segment: 'Segment',
      avgHistoric: 'Average past spend',
      avgPredicted: 'Average expected (12 months)',
      topTitle: 'Customers with the highest expected value',
      hiddenName: 'Customer (you cannot see names)',
      orders: '{{count}} orders',
      predictedLine: '{{value}} · ~{{orders}} orders',
      customerCell: 'Predicted value (12 months)',
      customerCellSub: '~{{orders}} orders expected · the model is explained on the Customer Value screen',
    },
    settings: {
      statusTitle: 'Status',
      statusLead:
        'AI is a paid external service. Even with a key entered, nothing is sent until you set a monthly spending limit and give explicit approval.',
      on: 'On',
      off: 'Off',
      enabledBy: 'Turned on by {{name}} on {{date}}',
      textProvider: 'Text (Anthropic Claude)',
      imageProvider: 'Images (OpenAI Images)',
      toIntegrations: 'Enter the key on the Integrations screen',
      enable: 'Turn on AI',
      disable: 'Turn off',
      disabled: 'AI turned off. No further charges unless a request is still running.',
      enableTitle: 'Turn on AI',
      enableLead:
        'Once on, authorised users can generate drafts and the provider (Anthropic/OpenAI) charges per request. The system stops at the monthly limit.',
      enableLimit: 'Monthly spending limit (USD)',
      enableLimitHint:
        'No new requests are sent once the estimated spend reaches this amount. At least 1 USD.',
      enableAck:
        'I confirm this is a paid service, charges are paid to the provider and spending stays within the limit.',
      enableProviderNote:
        "We recommend also setting a spending limit in the provider's own console (Anthropic Console, OpenAI limits): the system's limit is an estimate.",
      enableSubmit: 'I agree, turn on',
      enabled: 'AI turned on.',
      monthTitle: 'This month',
      spent: 'Estimated spend',
      spentOf: '{{spent}} / {{limit}}',
      noLimit: 'No limit set',
      requests: 'Requests',
      requestsToday: 'Today {{count}} / {{limit}}',
      estimateNote:
        'Amounts are estimates from token counts and the price table below; the provider invoice is authoritative.',
      configTitle: 'Settings',
      monthlyLimit: 'Monthly spending limit (USD)',
      dailyLimit: 'Daily request limit',
      textModel: 'Text model',
      lightModel: 'Model for short tasks',
      lightModelHint: 'Used for short tasks such as titles, meta descriptions and alt text (cheaper).',
      imageModel: 'Image model',
      imageQuality: 'Image quality',
      imageQualities: { low: 'Low', medium: 'Medium', high: 'High' },
      imageReserve: 'Upper bound per image (USD)',
      imageReserveHint:
        'Reserved before each image request; used as the cost if the provider does not report token counts.',
      supportDrafts: "Send the customer's message for support reply drafts",
      supportDraftsHint:
        'Emails, phone numbers, ID numbers, IBANs and card numbers are masked; names and addresses cannot be removed. Update your data-transfer inventory and privacy notice with your lawyer first.',
      save: 'Save settings',
      saved: 'Settings saved.',
      pricesTitle: 'Price table',
      pricesLead:
        "Default prices were taken from the providers' pricing pages on {{date}}. Update them here if a provider changes prices.",
      model: 'Model',
      kind: 'Type',
      kinds: { text: 'Text', image: 'Image' },
      inputPrice: 'Input / 1M tokens',
      outputPrice: 'Output / 1M tokens',
      custom: 'Custom price',
      unknownPrice: 'No price',
      editPrice: 'Edit price',
      resetPrice: 'Reset to default',
      priceTitle: 'Price of {{model}}',
      priceSaved: 'Price saved.',
      usageTitle: 'Usage',
      usageLead: 'Every request sent to a provider. Prompts and responses are not stored.',
      month: 'Month',
      totals: '{{requests}} requests · {{ok}} succeeded · {{errors}} errors · {{cost}}',
      tokens: '{{input}} input / {{output}} output tokens',
      byPurpose: 'By purpose',
      purpose: 'Purpose',
      cost: 'Cost',
      recent: 'Recent requests',
      when: 'Time',
      user: 'User',
      status: 'Result',
      masked: 'Masked',
      claims: 'Health claims',
      noUsage: 'No requests this month.',
      system: 'System',
    },
  },
  integrationMessages: {
    youtube_ok: 'Key verified (region list read; 1 quota unit, free).',
    youtube_key_format: 'Wrong API key format (starts with "AIza", 39 characters).',
    youtube_key_invalid: 'Google did not accept this API key.',
    youtube_key_restricted: 'A browser/app restriction on the key blocks server use (use an IP restriction).',
    youtube_api_disabled: 'YouTube Data API v3 is not enabled in the Google Cloud project.',
    youtube_quota: 'The YouTube daily quota is used up.',
    youtube_unreachable: 'YouTube could not be reached.',
    ai_text_ok: 'Key verified (model list read; no charge).',
    ai_image_ok: 'Key verified (model list read; no charge).',
    ai_invalid_key: 'The provider rejected the API key.',
    ai_invalid_settings: 'The API key is empty or malformed.',
    ai_provider_rate_limited: 'Provider rate limit hit; try again shortly.',
    ai_provider_overloaded: 'The provider is busy; try again shortly.',
    ai_provider_rejected: 'The provider rejected the request.',
    ai_provider_timeout: 'The provider did not respond in time.',
    ai_provider_unreachable: 'Could not reach the provider.',
    ai_provider_bad_response: "The provider's response could not be read.",
  },
  integrations: {
    ai_text: {
      name: 'AI (text) — Anthropic Claude',
      description:
        'Product descriptions, article and email drafts, translation and Copilot. Paid; use requires owner approval and a monthly limit in AI settings.',
    },
    ai_image: {
      name: 'AI (images) — OpenAI Images',
      description:
        'Article cover and inline images. Paid; use requires owner approval and a monthly limit in AI settings.',
    },
    youtube: {
      name: 'YouTube Data API',
      description:
        'Details of product and blog videos (thumbnail, duration, availability): video structured data on the storefront, hiding and flagging removed videos. Read-only, API key; free daily quota.',
    },
  },
  permissions: {
    'ai.generate': 'Generate drafts with AI (paid service)',
    'ai.usage.read': 'View AI usage and cost',
    'ai.settings.manage': 'Turn AI on/off and set the spending limit',
  },
  permissionModules: { ai: 'AI' },
  integrationFields: { organizationId: 'Organization ID (optional, org-…)' },
  auditActions: {
    'seo.keywords_clustered': 'Keyword clusters applied',
    'catalog.product.merchant_title': 'Google product title changed',
    'file.ai_generated': 'Image generated with AI',
    'file.provenance_update': 'Image source and licence updated',
    'article_project.create': 'Article project created',
    'article_project.update': 'Article project updated',
    'article_project.archive': 'Article project archived',
    'article_project.exported': 'Article project exported to blog',
    'article_project.ai_step': 'AI step in article project',
    'article_project.sources_checked': 'Article sources verified',
    'article_project.duplicates_checked': 'Article duplicate check',
    'ai.enabled': 'AI turned on',
    'ai.disabled': 'AI turned off',
    'ai.settings_update': 'AI settings updated',
    'recommendations.settings_update': 'Recommendation settings updated',
    'recommendations.recomputed': 'Recommendations recomputed',
    'forecast.settings_update': 'Demand forecast settings updated',
    'forecast.variant_update': 'Variant forecast setting updated',
  },
};
